import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppointmentRequestActivityType, WorkflowOrigin } from '@warema/shared/models';
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ActivityDataService } from '../activity/services/activity-data.service';
import { ProductCategoryDataService } from '../product-category/services/product-category-data.service';
import { SearchDataService } from '../search/services/search-data.service';

@Injectable({
    providedIn: 'root',
})
export class DirectSearchTriggerService {
    private readonly _productCategoryDataService = inject(ProductCategoryDataService);
    private readonly _activityDataService = inject(ActivityDataService);
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _searchDataService = inject(SearchDataService);

    private _isRegionalSea = false;
    private _noActivityProvided = false;

    public isDirectSearchTriggerMode$ = new BehaviorSubject<boolean>(false);
    public isDirectSearchTriggerModeFromSem$ = new BehaviorSubject<boolean>(false);
    public directSearchTriggeerAddress$ = new BehaviorSubject<string>('');
    public initialSearchCompleted = false;

    constructor() {
        this._activatedRoute.queryParams
            .pipe(
                switchMap(params => {
                    return this._searchDataService.selectedCompany$.pipe(
                        filter(x => !x),
                        map(() => params),
                    );
                }),
            )
            .subscribe(params => {
                const originParam = params['origin'] as WorkflowOrigin;
                const cityParam = params['city'] as string;
                const location = params['location'] as string;
                this._noActivityProvided = !(params['activity'] as string);

                // Default Search and use cases, where the UI layouts of the search were slightly modified
                const defaultSearchOrigins = [
                    WorkflowOrigin.WaremaPlusDefault,
                    WorkflowOrigin.WaremaCom,
                    WorkflowOrigin.SunCenter,
                ];

                const isDefaultSearchRedirect =
                    (!originParam || defaultSearchOrigins.includes(originParam)) && location && location !== '';

                const isDirectSearchTriggerModeFromSem =
                    this.isDirectSearchTriggerModeFromSem$.getValue() &&
                    originParam === WorkflowOrigin.SunExperienceManager;

                this._isRegionalSea =
                    !!originParam && originParam === WorkflowOrigin.RegionalSea && !!cityParam && cityParam !== '';
                const isDirectSearchTriggerMode =
                    isDefaultSearchRedirect || this._isRegionalSea || isDirectSearchTriggerModeFromSem;

                if (isDirectSearchTriggerMode) {
                    this.isDirectSearchTriggerMode$.next(true);

                    const triggerAddress = this._isRegionalSea ? cityParam : location;
                    this.directSearchTriggeerAddress$.next(triggerAddress);
                }
            });
    }

    public initializeStoreForDirectSearchTrigger(): void {
        this._productCategoryDataService.initService();

        if ((this._isRegionalSea && this._noActivityProvided) || this._noActivityProvided) {
            this._activityDataService.selectActivity(AppointmentRequestActivityType.ConsultationAtHome);
        }
    }

    public completeDirectSearchTriggerFlow(): void {
        this.initialSearchCompleted = this.isDirectSearchTriggerMode$.getValue();
    }
}
